import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setSentryUser } from './sentry';

export const AuthContext = createContext(null);

// Safer surface over AuthContext. Existing consumers can keep using
// useContext(AuthContext) — this hook is additive. New code should
// prefer useAuth() because:
//   - patchUser() is a guarded setUser that prevents the
//     spread+reassign foot-gun (writing setUser({ providerProfile })
//     directly nulls every sibling field — same shape as the
//     Mongoose subdoc hazard documented in CLAUDE.md).
//   - Derived selectors centralize the role/identity checks that
//     50+ components were reinventing inline.
//   - logout() centralizes the POST + clear-user pair.
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) return { user: null, loading: true };
  const { user, loading, setUser, login, checkAuth } = ctx;
  return {
    user, loading, login, checkAuth, setUser,
    // Guarded setter — accepts a patch object (merged shallow over
    // prev) or a functional updater (full control). Never lets a
    // caller pass a partial object that would wipe sibling fields.
    patchUser: (patch) => setUser(prev => {
      if (!prev) return prev;
      if (typeof patch === 'function') return patch(prev);
      return { ...prev, ...patch };
    }),
    isAuthenticated: !!user,
    isProvider: user?.accountType === 'PROVIDER',
    isClient: user?.accountType === 'CLIENT',
    isAdmin: !!user?.isAdmin,
    displayName: user?.profile?.fullName || user?.email || '',
    tz: user?.providerProfile?.timezone || 'America/Los_Angeles',
    logout: async () => {
      try { await axios.post('/api/auth/logout'); } catch {}
      setUser(null);
    },
  };
};

axios.defaults.withCredentials = true;
axios.defaults.headers.common['Accept'] = 'application/json';
axios.defaults.headers.post['Content-Type'] = 'application/json';

// Use environment variable for API URL or fallback to same origin in production
const baseURL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : window.location.origin);

axios.defaults.baseURL = baseURL;

console.log('Auth Context initialized with:', { baseURL, currentHost: window.location.hostname });

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      console.log('Checking auth with baseURL:', baseURL);
      const response = await axios.get('/api/auth/check-session', {
        withCredentials: true,
        headers: {
          'Accept': 'application/json'
        }
      });
      console.log('Check auth response:', response.data);
      if (response.data.isAuthenticated) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Check auth error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setUser(null);
    }
  };

  // Tag browser error reports with who was signed in (id only, no name or
  // email), so "is this everyone or just one provider?" is answerable.
  useEffect(() => {
    setSentryUser(user?._id || null);
  }, [user]);

  // Durable breadcrumb: this browser has held a CLIENT account for this
  // provider. After the session expires (24h TTL), the public provider
  // page uses it to offer "welcome back — sign in" instead of silently
  // showing the standard menu to someone who has an account. Never
  // cleared on logout — the fact that an account exists here doesn't
  // stop being true. See WelcomeFork.js.
  useEffect(() => {
    const jc = user?.accountType === 'CLIENT' ? user?.provider?.joinCode : null;
    if (jc) {
      try { window.localStorage.setItem(`av.returning.${jc}`, '1'); } catch { /* private mode */ }
    }
    // Generic marker: this browser has held a session, any account type.
    // Lets "/" route a returning-but-signed-out visitor to login instead
    // of the marketing page — same courtesy the installed PWA gets.
    if (user) {
    }
  }, [user]);

  useEffect(() => {
    checkAuth().finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    try {
      console.log('Attempting login with:', { email, baseURL });
      
      const response = await axios.post('/api/auth/login', 
        { email, password },
        {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Login response:', response.data);
      
      if (response.data.user) {
        setUser(response.data.user);
        return { success: true };
      }
    } catch (error) {
      console.error('Login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      throw error.response?.data?.message || 'Login failed';
    }
    return { success: false };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      setUser,
      login,
      checkAuth
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
